import { useState, useRef, useEffect } from "react";
import { UploadBox, StatusCard, SuccessCard, ErrorCard, Toast } from "./components/shared/WorkflowComponents";
import env from "../../configs/envConfig";
import "./App.css";

const ALLOWED_REQUIREMENTS = [".pdf", ".doc", ".docx", ".txt", ".rtf"];
const ALLOWED_RESUMES = [".pdf", ".doc", ".docx", ".txt", ".rtf", ".zip"];
const API_URL = env.REACT_APP_RESUME_PARSER_API_URL ?? "http://localhost:8000";
const MAX_FILES = 300;

const STEPS = [
  "Parsing requirements...",
  "Parsing resumes...",
  "Generating interview questions...",
  "Building report...",
];

export default function InterviewQuestions() {
  const [reqFile, setReqFile]           = useState(null);
  const [resumeFiles, setResumeFiles]   = useState([]);
  const [status, setStatus]             = useState("idle");
  const [stepIdx, setStepIdx]           = useState(0);
  const [downloadUrl, setDownloadUrl]   = useState(null);
  const [downloadName, setDownloadName] = useState("");
  const [errorMsg, setErrorMsg]         = useState("");
  const [toast, setToast]               = useState(null);
  const readerRef = useRef(null);

  const showToast = (msg) => {
    setToast(msg);
  };

  const stopReading = () => {
    if (readerRef.current) {
      readerRef.current.cancel();
      readerRef.current = null;
    }
  };

  useEffect(() => () => stopReading(), []);

  const handleRun = async () => {
    if (!reqFile || resumeFiles.length === 0) return;
    setStatus("running");
    setStepIdx(0);
    setDownloadUrl(null);
    setErrorMsg("");
    setToast(null);

    try {
      const formData = new FormData();
      formData.append("requirements", reqFile);
      resumeFiles.forEach(file => formData.append("resumes", file));

      const res = await fetch(`${API_URL}/interview-questions`, { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Unknown error" }));
        throw new Error(err.detail || "Server error");
      }

      const reader = res.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = "";
      let completed = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (!completed) {
            setErrorMsg("Processing timed out. This typically happens with large resume batches. Please try again with fewer resumes, or contact your administrator if the issue persists.");
            setStatus("error");
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.step === -1) {
              setErrorMsg(event.detail || "An unknown error occurred.");
              setStatus("error");
              return;
            }
            if (event.message === "done") {
              setDownloadUrl(`${API_URL}/download/${event.filename}`);
              setDownloadName(event.filename);
              setStepIdx(STEPS.length);
              setStatus("done");
              completed = true;
              return;
            }
            setStepIdx(event.step ?? 0);
          } catch {
            // ignore non-JSON lines (keepalive comments)
          }
        }
      }
    } catch (err) {
      const isTimeout =
        err.message?.toLowerCase().includes("network") ||
        err.message?.toLowerCase().includes("timeout") ||
        err.name === "TypeError";
      setErrorMsg(
        isTimeout
          ? "Processing timed out. This typically happens with large resume batches. Please try again with fewer resumes, or contact your administrator if the issue persists."
          : err.message
      );
      setStatus("error");
    }
  };

  const handleReset = () => {
    stopReading();
    setStatus("idle");
    setReqFile(null);
    setResumeFiles([]);
    setDownloadUrl(null);
    setErrorMsg("");
    setStepIdx(0);
  };

  const canRun = reqFile && resumeFiles.length > 0 && status !== "running";

  return (
    <div className="page">
      <div className="container">

        <div className="header">
          <h1>Interview Questions Generator</h1>
          <p>Upload requirements and resumes to generate tailored interview questions</p>
        </div>

        <div className="upload-row">
          <UploadBox
            label="📋 Job Requirements"
            icon="📋"
            multiple={false}
            limitHint="Limited to 1 requirement file"
            onFiles={([file]) => setReqFile(file)}
            onInvalid={showToast}
            allowed={ALLOWED_REQUIREMENTS}
            disabled={status === "running"}
          />
          <UploadBox
            label="👤 Resumes"
            icon="👤"
            multiple={true}
            limitHint={`Limit to ${MAX_FILES} resumes. Please split into multiple runs, if needed.`}
            onFiles={newFiles => setResumeFiles(prev => {
              const names = new Set(prev.map(file => file.name));
              const filtered = newFiles.filter(file => !names.has(file.name));
              const combined = [...prev, ...filtered];
              if (combined.length > MAX_FILES) {
                showToast(`File limit exceeded. You can upload a maximum of ${MAX_FILES} files. ${combined.length - MAX_FILES} were not added.`);
                return combined.slice(0, MAX_FILES);
              }
              return combined;
            })}
            onInvalid={showToast}
            allowed={ALLOWED_RESUMES}
            disabled={status === "running"}
          />
        </div>

        <div className="upload-file-row">
          <div className="upload-column">
            {reqFile && (
              <div className="file-badge single">
                <span>📄 {reqFile.name}</span>
                <button onClick={() => setReqFile(null)}>×</button>
              </div>
            )}
          </div>
          <div className="upload-column">
            {resumeFiles.length > 0 && (
              <div className="resume-list">
                {resumeFiles.map(file => (
                  <div key={file.name} className="file-badge multi">
                    <span>📄 {file.name}</span>
                    <button onClick={() => setResumeFiles(prev => prev.filter(existingFile => existingFile.name !== file.name))}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {status !== "done" && (
          <div style={{ textAlign: "center", marginTop: "8px" }}>
            <button
              onClick={handleRun}
              disabled={!canRun}
              className={`run-btn ${canRun ? "ready" : "disabled"}`}
            >
              {status === "running" ? "Generating..." : "Generate Questions"}
            </button>
          </div>
        )}

        {status === "idle" && (
          <p className="hint">
            {!reqFile && resumeFiles.length === 0
              ? "Upload a requirements file and at least one resume to begin"
              : !reqFile
              ? "Upload a requirements file to begin"
              : resumeFiles.length === 0
              ? "Upload at least one resume to begin"
              : "Press Generate Questions to create interview questions"}
          </p>
        )}

        <p className="ai-disclaimer">
          ⚠️ Questions are AI-generated and should be reviewed before use in actual interviews.
        </p>

        <Toast message={toast} onClose={() => setToast(null)} />

        {status === "running" && (
          <StatusCard steps={STEPS} currentStep={stepIdx} />
        )}

        {status === "done" && (
          <SuccessCard
            downloadUrl={downloadUrl}
            downloadName={downloadName}
            onReset={handleReset}
            onDownloadError={showToast}
          />
        )}

        {status === "error" && (
          <ErrorCard
            errorMsg={errorMsg}
            onRetry={() => setStatus("idle")}
          />
        )}

      </div>
    </div>
  );
}
